import { LOCALE } from './localeValidationConstants';

export interface FieldValidationParams {
  type?: string;
  name: string;
  required?: boolean;
  locale: string;
}

type MinMax = {
  value: number;
  message: string;
};

type Range = {
  min: MinMax;
  max: MinMax;
};

type ValidationParams = {
  required?: string;
  pattern?: { value: RegExp; message: string };
  min?: MinMax;
  max?: MinMax;
  minLength?: MinMax;
  maxLength?: MinMax;
  // eslint-disable-next-line no-unused-vars
  validate?: (value: string) => boolean | string;
};

export const generateValidationParams = ({
  type = 'text',
  name = '',
  required = false,
  locale = 'UA',
}: FieldValidationParams) => {
  let validationParams: ValidationParams = {};

  let patternValue: RegExp | null = null;
  let range: Range | null = null;

  switch (type) {
    case 'email':
      patternValue =
        /^(?=^.{3,63}$)(^[A-Za-z0-9']{3,}(([_.-](?=[A-Za-z0-9]))[a-zA-Z0-9]+([-.](?=[A-Za-z0-9]))*?)*@(\w+([.-](?=(\w|\d))))+[a-zA-Z]{2,6})$/;
      break;
    case 'tel':
      patternValue = /^\+?[\d\s]{6,17}$/;
      break;
    case 'number':
      patternValue = /^\d+$/;
      break;
    case 'textarea':
      switch (name) {
        case 'comment':
          patternValue = /^(?!.*(<\/?[^>]+>|https?:\/\/\S+|http:\/\/\S+)).*$/gs;
          break;
      }
      break;
    case 'text':
      switch (name) {
        case 'formReporterName':
          patternValue =
            locale == 'UA'
              ? /^.[a-zA-Zа-яА-ЯёЁЇїІіЄєҐґ 'ʼ`-]{1,}$/gm
              : /^.[a-zA-Z 'ʼ`-]{1,}$/gm;
          range = {
            min: {
              value: 2,
              message:
                locale == 'UA'
                  ? LOCALE.UA.MINIMUM_TEXT + 2
                  : LOCALE.EN.MINIMUM_TEXT + 2,
            },
            max: {
              value: 100,
              message:
                locale == 'UA'
                  ? LOCALE.UA.MAXIMUM_TEXT + 100
                  : LOCALE.EN.MAXIMUM_TEXT + 100,
            },
          };
          break;
        case 'formReporterStatus':
          patternValue = /^[\p{L}\p{P}\s'-]{4,40}$/u;
          break;
        case 'formPatientName':
          patternValue = /^.[A-Z 'ʼ`ĄĆĘŁŃÓŚŹŻÁÉÍÑÓÚĂÂÎȘȚА-ЯЁЇІЄҐ-]{1,}$/gm;
          range = {
            min: {
              value: 2,
              message:
                locale == 'UA'
                  ? LOCALE.UA.MINIMUM_TEXT + 2
                  : LOCALE.EN.MINIMUM_TEXT + 2,
            },
            max: {
              value: 100,
              message:
                locale == 'UA'
                  ? LOCALE.UA.MAXIMUM_TEXT + 100
                  : LOCALE.EN.MAXIMUM_TEXT + 100,
            },
          };
          break;
        case 'formPatientAge':
          patternValue = /^(?:\d|[1-9]\d|1[01]\d|120)$/;
          break;
        case 'formPatientGender':
          patternValue = /^[\p{L}]{4,10}$/u;
          break;
        case 'formDrugName':
        case 'formDrugManufacturer':
        case 'formDrugMedicalForm':
          patternValue = /^[\p{L}\s'’`.«»"“”-]{4,50}$/u;

          break;
        case 'formDrugSerial':
          patternValue = /^[\w-]{5,20}$/;
          break;
        case 'feedbackFormName':
          patternValue =
            locale == 'UA'
              ? /^.[a-zA-Zа-яА-ЯёЁЇїІіЄєҐґ 'ʼ`-]{1,}$/gm
              : /^.[a-zA-Z 'ʼ`-]{1,}$/gm;
          range = {
            min: {
              value: 2,
              message:
                locale == 'UA'
                  ? LOCALE.UA.MINIMUM_TEXT + 2
                  : LOCALE.EN.MINIMUM_TEXT + 2,
            },
            max: {
              value: 100,
              message:
                locale == 'UA'
                  ? LOCALE.UA.MAXIMUM_TEXT + 100
                  : LOCALE.EN.MAXIMUM_TEXT + 100,
            },
          };
          break;
        default:
          patternValue = null;
      }
      break;
    default:
      patternValue = null;
  }

  if (required) {
    validationParams = {
      ...validationParams,
      required:
        locale == 'UA'
          ? LOCALE.UA.REQUIRED
          : LOCALE.EN.REQUIRED || 'This field is required',
      validate: (value: string) =>
        value.trim() !== '' ||
        (locale == 'UA'
          ? 'Значення не може бути лише з пробілів'
          : 'Value cannot be only spaces'),
    };
  }

  if (range) {
    validationParams =
      type != 'number'
        ? {
            ...validationParams,
            minLength: range.min,
            maxLength: range.max,
          }
        : {
            ...validationParams,
            min: range.min,
            max: range.max,
          };
  }

  return patternValue
    ? {
        ...validationParams,
        pattern: {
          value: patternValue,
          message:
            locale == 'UA'
              ? type == 'text'
                ? LOCALE.UA[
                    name
                      .replace(/([a-z])([A-Z])/g, '$1_$2')
                      .split('_')
                      .slice(1)
                      .join('_')
                      .toUpperCase()
                  ]
                : LOCALE.UA[type.toUpperCase()]
              : type == 'text'
              ? LOCALE.EN[
                  name
                    .replace(/([a-z])([A-Z])/g, '$1_$2')
                    .split('_')
                    .slice(1)
                    .join('_')
                    .toUpperCase()
                ]
              : LOCALE.EN[type.toUpperCase()] || 'Please enter valid data',
        },
      }
    : validationParams;
};
